// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The single way this app talks to the server (design doc §9.1, §12.1.1, §14).
 *
 * Everything that must happen on *every* request lives here rather than at call sites,
 * because a rule applied in twelve places is a rule that will be missing from the
 * thirteenth:
 *
 * - the CSRF token is copied out of the readable cookie into `X-CSRF-Token` on every
 *   unsafe method (§12.1.1 step 3);
 * - `Accept-Language` carries the current locale so the server's error messages come back
 *   in the language the user is reading (§14);
 * - every unsafe method carries this machine's clock in `X-Client-Time`, with its UTC
 *   offset: the SNs a filing is given are dated by the day on this screen, and the server
 *   refuses the clock when it is more than five minutes off its own (backend
 *   `core/client_clock.py`);
 * - a 401 means the session is gone, so the app navigates to the login endpoint with a
 *   `return_to` pointing at where the user was;
 * - the error envelope is parsed into a typed `ApiError` carrying the stable `code`. UI
 *   branches on the code and never on the message (§9.1) — the message is localized prose
 *   and may be reworded at any time.
 */

import { CSRF_COOKIE, readCookie } from "./cookies";

declare const __CLIENT_VERSION__: string;

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly localizedMessage: string,
    readonly details: Record<string, unknown>,
    readonly requestId: string,
  ) {
    super(`${code} (HTTP ${status})`);
    this.name = "ApiError";
  }

  /** §8.6: the archive moved under the user. The caller shows the conflict dialog. */
  get isVersionConflict(): boolean {
    return this.code === "ARCHIVE_VERSION_CONFLICT";
  }

  /** The version the server says is current, when it told us. */
  get latestVersion(): number | undefined {
    const latest = this.details.latest_version;
    return typeof latest === "number" ? latest : undefined;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Required by the server on every creating operation (§7.7). */
  idempotencyKey?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Returns the raw Response — for CSV downloads, which are not JSON. */
  raw?: boolean;
  signal?: AbortSignal;
}

let currentLocale = "zh-CN";

export function setApiLocale(locale: string): void {
  currentLocale = locale;
}

/** The UI's language, which a remark is written in (design doc §7.3). */
export function apiLocale(): string {
  return currentLocale;
}

/** Where an expired session sends the browser. Replaceable so tests do not navigate. */
export let onUnauthenticated: () => void = () => {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/auth/login?return_to=${returnTo}`);
};

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // `URLSearchParams` encodes the value, which matters for timestamps: an unencoded
    // `+00:00` offset is read as a space and the server cannot parse it as a datetime.
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": currentLocale,
    "X-Client-Version": __CLIENT_VERSION__,
  };

  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    // Sent when present rather than thrown on: if the cookie is missing the session is
    // already gone, and the server's 401 is a better diagnosis than a client-side guess.
    if (csrf) headers["X-CSRF-Token"] = csrf;
    headers["X-Client-Time"] = localTimestamp(new Date());
  }
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    // No Content-Type: the browser has to set the multipart boundary itself.
    body = options.body;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    body,
    // Same-origin deployment (§3.1), so the session cookie rides along by default; stated
    // explicitly because a future split origin would silently stop sending it.
    credentials: "same-origin",
    signal: options.signal,
  });

  if (response.status === 401) {
    onUnauthenticated();
    throw await toApiError(response);
  }
  if (!response.ok) throw await toApiError(response);

  if (options.raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = "UNKNOWN";
  let message = response.statusText;
  let details: Record<string, unknown> = {};
  let requestId = response.headers.get("X-Request-ID") ?? "";
  try {
    const body = await response.json();
    code = body.code ?? code;
    message = body.message ?? message;
    details = body.details ?? {};
    requestId = body.request_id ?? requestId;
  } catch {
    // A non-JSON error body means something upstream failed before the app — a proxy, or
    // the container being down. The status and request id are all there is to report.
  }
  return new ApiError(response.status, code, message, details, requestId);
}

/** A fresh idempotency key. The server requires UUID format (§7.7). */
/** `2026-10-29T09:30:00.000+08:00`: the local wall-clock time with its offset, which
 *  `toISOString` (always UTC) would lose — and the offset is what says which day it is here. */
export function localTimestamp(date: Date): string {
  const pad = (value: number, width = 2) => String(Math.abs(value)).padStart(width, "0");
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `.${pad(date.getMilliseconds(), 3)}` +
    `${sign}${pad(Math.trunc(offset / 60))}:${pad(offset % 60)}`
  );
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
